import { Component, DestroyRef, effect, ElementRef, inject, input, output, signal, viewChildren } from '@angular/core';
import { DigitsOnlyInputDirective } from '../../directives/digits-only-input.directive';
import { AbstractControl, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectOnFocusDirective } from '../../directives/select-on-focus.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { OtpDestinationType } from '../../enums/otp-destination-type.enum';
import { OtpRequestClientDto } from '../../dtos/otp-request.dto';
import { AutofocusDirective } from '../../directives/autofocus.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Observable, timer } from 'rxjs';
import { OTP_LENGTH } from '../../constants';
import { map } from 'rxjs/operators';
import { PreloaderComponent } from '../preloader/preloader.component';

@Component({
  selector: 'tc-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
  imports: [
    DigitsOnlyInputDirective,
    FormsModule,
    SelectOnFocusDirective,
    TranslatePipe,
    ReactiveFormsModule,
    AutofocusDirective,
    AsyncPipe,
    PreloaderComponent,
  ],
})
export class OtpComponent {
  protected readonly OtpDestinationType = OtpDestinationType;
  private readonly destroyRef = inject(DestroyRef);

  submitLabel = input<string>();
  otpRequest = input.required<OtpRequestClientDto>();
  otpDigitsFormArray = input.required<FormArray<FormControl<string>>>();
  isLoading = input<boolean>(false);
  error = input<string>();
  submit = output<string>();
  resendOtp = output<void>();

  otpInputs = viewChildren<ElementRef<HTMLInputElement>>('otpInput');

  isFormErrorsVisible = signal<boolean>(false);

  canResendOtp$: Observable<boolean>;
  nextOtpRetryTimeLeft$: Observable<string>;

  constructor() {
    effect(() => {
      this.otpDigitsFormArray().valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.isFormErrorsVisible.set(false));
    });

    effect(() => {
      const otpRequest = this.otpRequest();
      if (!otpRequest) {
        this.canResendOtp$ = null;
        this.nextOtpRetryTimeLeft$ = null;
        return;
      }

      const nextRetryTime = new Date(otpRequest.nextRetryAtIso).getTime();
      this.canResendOtp$ = timer(0, 500).pipe(map(() => {
        return Date.now() > nextRetryTime;
      }));
      this.nextOtpRetryTimeLeft$ = timer(0, 1000).pipe(map(() => {
        const timeLeftMs = nextRetryTime - Date.now();
        const sec = Math.ceil(timeLeftMs / 1000) % 60;
        let min = Math.floor(timeLeftMs / (60 * 1000));
        if (sec === 0) {
          min += 1;
        }
        const secPadded = `${sec}`.padStart(2, '0');
        return `${min}:${secPadded}`;
      }));
    });
  }

  isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  onBackspace(otpDigitIndex: number): void {
    if (otpDigitIndex === 0 || this.otpDigitsFormArray().at(otpDigitIndex).getRawValue()) {
      return;
    }
    this.focusOtpInput(otpDigitIndex - 1);
  }

  onInputEvent(otpDigitIndex: number, evt: InputEvent): void {
    if (evt.inputType === 'insertFromPaste') {
      return;
    }

    const value = this.otpDigitsFormArray().at(otpDigitIndex).getRawValue();
    if (value.length > 1) { // Handle case of phones pasting suggested OTP code
      this.fillOtpCode(otpDigitIndex, value, evt);
      return;
    }

    const isLastControl = otpDigitIndex === (this.otpDigitsFormArray().length - 1);
    if (isLastControl && this.otpDigitsFormArray().valid) {
      this.onSubmit();
    } else if (this.otpDigitsFormArray().at(otpDigitIndex).valid) {
      this.focusOtpInput(otpDigitIndex + 1);
    }
  }

  onPaste(otpDigitIndex: number, event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted) return;

    this.fillOtpCode(otpDigitIndex, pasted, event);
  }

  private fillOtpCode(otpDigitIndex: number, otpCodeStr: string, event: Event): void {
    const otpDigitsRegex = new RegExp(`^\\d{${OTP_LENGTH}}$`);

    const digitsParts = otpCodeStr
      .split(' ')
      .map(part => part.trim())
      .filter(part => part.length === OTP_LENGTH && otpDigitsRegex.test(part));
    let otpCode = digitsParts.find(part => part.trim().length === OTP_LENGTH);
    if (!otpCode) {
      otpCode = digitsParts[0];
    }

    otpCode.split('').forEach((digit, i) => {
      const otpDigitIndexToPaste = otpDigitIndex + i;
      this.otpDigitsFormArray().at(otpDigitIndexToPaste)?.setValue(digit);
    });

    // prevent triggering "input" event
    event.preventDefault();
    event.stopPropagation();

    if (this.otpDigitsFormArray().at(otpDigitIndex).valid) {
      const otpDigitIndexToFocus = Math.max(otpDigitIndex + otpCode.length - 1, this.otpDigitsFormArray.length - 1);
      this.focusOtpInput(otpDigitIndexToFocus);
    }

    if (this.otpDigitsFormArray().valid) {
      this.onSubmit();
    }
  }

  focusFirstInvalidOtpInput(): void {
    const firstInvalidIndex = this.otpDigitsFormArray().controls.findIndex(control => control.invalid);
    if (firstInvalidIndex !== -1) {
      this.focusOtpInput(firstInvalidIndex);
    } else {
      this.focusOtpInput(0);
    }
  }

  focusOtpInput(otpDigitIndex: number): void {
    setTimeout(() => this.otpInputs().at(otpDigitIndex)?.nativeElement.focus(), 0);
  }

  onSubmit(): void {
    if (this.isLoading()) {
      return;
    }

    this.isFormErrorsVisible.set(true);
    if (this.otpDigitsFormArray().invalid) {
      this.focusFirstInvalidOtpInput();
      return;
    }

    this.submit.emit(this.otpDigitsFormArray().getRawValue().join(''));
  }

  onResendOtp(): void {
    this.otpDigitsFormArray().reset();
    this.isFormErrorsVisible.set(false);
    this.resendOtp.emit();
    this.focusOtpInput(0);
  }

  protected readonly OTP_LENGTH = OTP_LENGTH;
}
